import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { MoreHorizontal, Trash2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { type ViewMode } from "~/hooks/useViewMode";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { UserAvatar } from "~/pages/manifold-nextgen/components/users/UserAvatar";
import { nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { ProjectListItem, deleteProject, projectListPath } from "~/services/projects";
import { DeleteProjectDialog } from "./DeleteProjectDialog";

interface ProjectCardProps {
  project: ProjectListItem;
  view?: ViewMode;
}

const MAX_TEAM_AVATARS = 5;
const MAX_DATASET_ICONS = 5;

const TeamAvatars = ({ project, ringClass }: { project: ProjectListItem; ringClass: string }) => {
  const agents = project.agents;
  return (
    <div className="flex items-center">
      {agents.slice(0, MAX_TEAM_AVATARS).map((agent, i) => (
        <AgentAvatar
          key={agent.id}
          id={agent.id}
          name={agent.name}
          size={24}
          title={agent.display_name}
          className={cn(
            `relative ring-2 ${ringClass} transition-transform duration-75 hover:z-10 hover:scale-110`,
            i > 0 && "-ml-1"
          )}
        />
      ))}
      <UserAvatar
        userId={project.owner_user_id}
        size={24}
        className={cn(
          `relative ring-2 ${ringClass} transition-transform duration-75 hover:z-10 hover:scale-110`,
          agents.length > 0 && "-ml-1"
        )}
        title="Owner"
      />
    </div>
  );
};

export const ProjectCard = ({ project, view = "card" }: ProjectCardProps) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteProject(project.short_id);
      await mutate(projectListPath());
      showSuccess(`"${project.name}" deleted`);
    } catch {
      showError("Failed to delete project");
    }
  };

  const visibleDatasetCount = Math.min(project.dataset_count, MAX_DATASET_ICONS);
  const extraDatasetCount = project.dataset_count - visibleDatasetCount;

  const updatedAt = formatDistanceToNowStrict(new Date(project.updated_at), { addSuffix: true });

  const dropdown = (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "size-7 shrink-0 text-muted-foreground",
              "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
              "focus-visible:ring-0 focus-visible:border-transparent"
            )}
            aria-label={`Actions for ${project.name}`}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  if (view === "list") {
    return (
      <>
        <div className="group relative">
          <div
            className={cn(
              "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
              "cursor-pointer transition-colors duration-150 group-hover:bg-muted"
            )}
            onClick={() => navigate(nextgenProjectPath(project.short_id))}
          >
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-semibold text-card-foreground">
                {project.name}
              </h3>
              <p
                className={cn(
                  "truncate text-xs text-muted-foreground",
                  !project.description && "italic text-muted-foreground/60"
                )}
              >
                {project.description || "No description"}
              </p>
            </div>
            <div className="w-36 shrink-0">
              <TeamAvatars project={project} ringClass="ring-card" />
            </div>
            <p className="w-36 shrink-0 text-right text-xs text-muted-foreground">
              Updated {updatedAt}
            </p>
            {dropdown}
          </div>
        </div>
        <DeleteProjectDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          projectName={project.name}
          onConfirm={handleDelete}
        />
      </>
    );
  }

  return (
    <>
      <div className="group relative h-full transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02]">
        <Card
          className={cn(
            "relative h-full gap-0 overflow-hidden p-0",
            "cursor-pointer shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
          )}
          onClick={() => navigate(nextgenProjectPath(project.short_id))}
        >
          <div className="flex flex-col gap-3 px-5 pt-5 pb-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="min-w-0 flex-1 truncate text-base font-semibold leading-tight text-card-foreground group-hover:text-foreground">
                {project.name}
              </h3>
              {dropdown}
            </div>

            <p
              className={cn(
                "line-clamp-2 text-sm text-muted-foreground",
                !project.description && "italic text-muted-foreground/60"
              )}
            >
              {project.description || "No description"}
            </p>

            <div className="grid grid-cols-[5rem_1fr] items-center gap-x-3 gap-y-1.5 pt-1 text-xs">
              <span className="font-semibold uppercase tracking-wide text-muted-foreground">
                Datasets
              </span>
              <div className="flex items-center">
                {project.dataset_count === 0 ? (
                  <span className="text-muted-foreground/60">—</span>
                ) : (
                  <>
                    {Array.from({ length: visibleDatasetCount }).map((_, i) => (
                      <DatasetIcon
                        key={i}
                        className={cn(
                          "relative rounded-full ring-2 ring-card",
                          "transition-transform duration-75 hover:z-10 hover:scale-110",
                          i > 0 && "-ml-1"
                        )}
                      />
                    ))}
                    {extraDatasetCount > 0 && (
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        +{extraDatasetCount}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-5 py-2.5">
            <TeamAvatars project={project} ringClass="ring-card" />
            <span className="text-xs text-muted-foreground">Updated {updatedAt}</span>
          </div>
        </Card>

        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 rounded-xl",
            "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
            "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
          )}
        />
      </div>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectName={project.name}
        onConfirm={handleDelete}
      />
    </>
  );
};
